<template>
  <div class='wrapper-page'>
    <modal ref="deleteMessageForm">
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('communication.delete_messages') }}
        </h5>
      </template>
      <template #body>
        <form>
          <div class='modal-body'>
            <p>{{ $t('communication.are_you_sure_you_wanna_delete') }}</p>
          </div>
          <div class='modal-footer'>
            <input :value="$t('communication.delete_messages_button')" @click.prevent='deleteSentMessage'
                   class='btn btn-outline-danger float-right' type='reset'/>
          </div>
        </form>
      </template>
    </modal>

    <modal class="filter-all-messages" ref='filtersModal'>
      <template #header>
        <h5 class='modal-title mt-0'>
          {{ $t('shared.filter') }}
        </h5>
      </template>

      <template #body>
        <form @submit.prevent='filterSentEmails'>
          <div class='modal-body'>
            <div class='form-group form-inline'>
              <label>{{ $t('folders_list.starred_folders') }}</label>
              <label>
                <input type='radio' v-model='starred' value='true'>
                {{ $t('folders_list.yes_way') }}
              </label>

              <label>
                <input type='radio' v-model='starred' value='false'>
                {{ $t('folders_list.no_way') }}
              </label>
              <label>
                <i :title="$t('records_list.remove_selection')" @click.prevent='starred = null'
                   class='fas fa-times'></i>
              </label>
            </div>

            <div class="form-group">
              <label>{{ $t('communication.from') }}</label>
              <infinite-multi-select :getter="getUsers" v-model="fromUsersFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.to') }}</label>
              <infinite-multi-select :getter="getContacts" v-model="toContactFilters" />
            </div>
            <div class="form-group">
              <label>
                {{ $t('communication.status') }}
              </label>
              <multi-select :options="messageStatus" v-model="messageStatusFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.keywords') }}</label>
              <label>
                <input class="form-control" name="search" type="text" v-model="searchInSent">
              </label>
            </div>
            <div class="form-group">
              <label>{{ $t('communication.tags') }}</label>
              <infinite-multi-select :getter="getTags" v-model="tagsFilters" />
            </div>
            <div class="form-group">
              <label>{{ $t('communication.sent_date') }}</label>
              <div class="date">
                <label>{{ $t('communication.from') }}</label>
                <datepicker
                  :full-month-name="true"
                  :inline="false"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')"
                  :typeable="true"
                  input-class="form-control expiration-input"
                  name="document_request[sending_date]"
                  ref="datepicker"
                  v-model="fromSendingDate">
                </datepicker>
              </div>
              <div class="date">
                <label>{{ $t('communication.to') }}</label>
                <datepicker
                  :full-month-name="true"
                  :inline="false"
                  :monday-first="true"
                  :placeholder="$t('expiration.pick_date')"
                  :typeable="true"
                  @selected="this.$refs.datepicker.close()"
                  input-class="form-control expiration-input"
                  name="document_request[sending_date]"
                  ref="datepicker"
                  v-model="toSendingDate">
                </datepicker>
              </div>
            </div>
          </div>

          <div class='modal-footer'>
            <input :value="$t('folders_list.clear_filters')" @click.prevent='resetFilters' class='btn btn-neutral float-right'
                   type='reset'/>
            <input :value="$t('folders_list.filter')" class='btn btn-outline-primary float-right' type='submit'/>
          </div>
        </form>
      </template>
    </modal>

    <div class='container-fluid text-center'>
      <h1 class='page-title'>
        {{ $t('communication.sent') }} ({{ meta.total }})
        <a :href="sentVideo()" target="_blank" class="own-tooltip own-tooltip-bottom video-link" :data-tooltip="$t('communication.click_here_to_watch_sent')"><img src="../../assets/video.svg" /></a>
      </h1>
      <div class="actions-on-table">
        <div class='filter'>
          <a @click.prevent="openModal($refs.filtersModal)" class="filter-by btn btn-neutral" href="#">
            {{ $t('root.filter_all_data_by') }}
          </a>
        </div>
      </div>
      <div class="filter-results" v-if="filterMessages">
        <strong v-if="hasFilters">{{ $t('root.filter_results_for') }}</strong>
        <div v-if="starred !== null">
          <p>{{ $t('root.filters.starred') }}
            <strong v-if="starred === 'true'">{{ $t('root.filters.yes_way') }}</strong>
            <strong v-if="starred === 'false'">{{ $t('root.filters.no_way') }}</strong>
          </p>
        </div>
        <div v-if="fromUsersFilters.length > 0">
          <p>{{ $t('communication.filters.sending_date_from') }} <strong
            v-for="user in fromUsersFilters">{{ user.name }}</strong></p>
        </div>
        <div v-if="toContactFilters.length > 0">
          <p>{{ $t('communication.filters.sending_date_to') }} <strong
            v-for="contact in toContactFilters">{{ contact.name }}</strong></p>
        </div>
        <div v-if="messageStatusFilters.length > 0">
          <p>{{ $t('communication.filters.status') }} <strong
            v-for="status in messageStatusFilters">{{ status.name }}</strong></p>
        </div>
        <div v-if="searchInSent !== ''">
          <p>{{ $t('communication.filters.keywords') }} <strong>{{ searchInSent }}</strong></p>
        </div>
        <div v-if="tagsFilters.length > 0">
          <p>{{ $t('communication.filters.tags') }} <strong v-for="tag in tagsFilters">{{ tag.name }}</strong></p>
        </div>
        <div v-if="fromSendingDate !== null">
          <p>{{ $t('communication.filters.receiving_date') }} <strong>{{ formatDate(fromSendingDate) }}</strong></p>
        </div>
        <div v-if="toSendingDate !== null">
          <p>{{ $t('communication.filters.until_date') }} <strong>{{ formatDate(toSendingDate) }}</strong></p>
        </div>

        <a @click.prevent="resetFilters" href="#" v-if="hasFilters">{{ $t('records_list.clear_filters') }}</a>
      </div>
      <loading class="m-t-60" v-if="loading"/>
      <div class="empty-list-wrapper" v-else-if='sentEmails.length === 0 && !loading'>
        <img src="../../assets/empty_states/envelope.svg"/>
        <p>{{ $t('communication.sent_empty_state') }}</p>
      </div>
      <div class="table_wrapper" v-else-if='sentEmails.length > 0 && !loading'>
        <div :class="['bulk-container', { 'hide': bulkOpMessages.length === 0  }]">
          <span>{{ bulkOpMessages.length }} {{ $t('root.sent_messages') }}</span>
          <ul>
            <li>
              <a @click.prevent="$refs.bulkTagMessages.open(bulkOpMessages, null, null, null, null)" class="dropdown-item" href="#">
                <i class="fas fa-tag"></i>
                {{ $t('communication.tags') }}
              </a>
              <bulk-tag-messages @success="addTagsToBulkMessages()" ref="bulkTagMessages" />
            </li>
            <li>
              <a @click.prevent='openDeleteMessageModal(bulkOpMessages)' class="dropdown-item" href="#" rel="nofollow">
                <i class="far fa-trash-alt"></i>
                {{ $t('communication.delete') }}
              </a>
            </li>
          </ul>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th><input @input='selectAll' type='checkbox'/></th>
              <th></th>
              <th>{{ $t('communication.from') }}</th>
              <th>{{ $t('communication.to') }}</th>
              <th>{{ $t('communication.subject') }}</th>
              <th>{{ $t('communication.tags') }}</th>
              <th>{{ $t('communication.sent_on') }}</th>
              <th>{{ $t('communication.status') }}</th>
              <th>{{ $t('communication.actions') }}</th>
            </tr>
          </thead>
          <tbody>
          <tr v-for="message in sentEmails">
            <td class="bulk-check">
              <input :value='message.id' type='checkbox' v-model='bulkOpMessages'/>
            </td>
            <td>
              <a @click.prevent="starMessage(message)" class="starred" href='#'>
                <i :class="['fa-star', {'far': !message.starred, 'fas': message.starred }]"></i>
              </a>
            </td>
            <td>
              <router-link
                :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}">
                {{ message.creator.full_name }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}">
                {{ message.receiver.name }} - {{ message.receiver.email }}
              </router-link>
            </td>
            <td>
              <router-link
                :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}"
                v-if="message.subject">
                {{ message.subject }}
              </router-link>
              <router-link
                :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}"
                v-else>
                {{ $t('communication.no_subject') }}
              </router-link>
              <ul v-if="message.attached_records.length > 0" class="files-received">
                <li v-for="record in (message.attached_records.slice(0, 3))">
                  <img :src="mimeToSrc(record.mime_type)" />
                  <router-link :to="{name: 'record', params: { id: record.id }}" class="text-truncate file-name">
                    {{record.filename}}
                  </router-link>
                </li>
              </ul>
              <router-link :to="{name: 'message', params: { id: message.id, referrer: $route.name, messageType: $route.name }}" v-if="message.attached_records.length > 3" class="stay-blue">
                + {{ message.attached_records.length - 3}} {{ $t('communication.more_files') }}
              </router-link>
            </td>
            <td>
              <tag-manager
                :resource='message'
                @reload-list="getSentEmails()"
                resourceType="documentRequest" />
              <div class="tag-dropdown-column">
                <div :class="tag.color" class="existing-tags-wrapper"
                     v-for="tag in message.tags">
                  <div class="btn-group btn-group-toggle" data-toggle="buttons">
                    <label class="btn active">
                      <router-link :to="{name: 'tag', params: { id: tag.id }}">
                        {{ tag.name }}
                      </router-link>
                    </label>
                    <label @click.prevent="removeConnectedTag(tag, message.id)" class="btn">
                      <i class="fa fa-remove"></i>
                    </label>
                  </div>
                </div>
              </div>
            </td>
            <td>{{ formatDate(message.sent_on) }}</td>
            <td>
              <i :class="`fas fa-circle ${ statusClass(message.status) }`" />
              {{ statusText(message.status) }}
            </td>
            <td class="dropdown-column">
              <div class="dropdown">
                <button aria-expanded="false" aria-haspopup="true" class="btn dropdown-toggle" data-toggle="dropdown"
                        type="button">· · ·
                </button>
                <div class="dropdown-menu dropdown-menu-right">
                  <ul>
                    <li>
                      <a @click.prevent='openDeleteMessageModal([message.id])' class="dropdown-item" href="#"
                         rel="nofollow">
                        <i class="far fa-trash-alt"></i>
                        {{ $t('communication.delete') }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="empty-list-wrapper" v-else-if='sentEmails.length === 0 && !hasFilters'>
        <p>{{ $t('root.no_data') }}</p>
      </div>

      <div class="empty-list-wrapper" v-else-if='sentEmails.length === 0 && hasFilters'>
        <p>{{ $t('root.no_data_filters') }}</p>
        <a @click.prevent="$refs.filtersModal.reset" href="#">{{ $t('root.clear_filters') }}</a>
      </div>

      <pagination :meta='meta'/>
    </div>
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'

import _modal from '../../components/_modal'
import _pagination from '../../components/_pagination'
import _loading from '../../components/_loading'

import tag_manager from '../../components/tags/_tags_manager'
import bulk_tag_messages from '../../components/tags/_bulk_tag_messages'

import _multi_select from '../../components/shared/_multi_select'
import _infinite_multi_select from '../../components/shared/_infinite_multi_select'

export default {
  name: 'Sent',
  components: {
    modal: _modal,
    loading: _loading,
    pagination: _pagination,
    'datepicker': Datepicker,
    'tag-manager': tag_manager,
    'multi-select': _multi_select,
    'infinite-multi-select': _infinite_multi_select,
    'bulk-tag-messages': bulk_tag_messages
  },
  data() {
    return {
      loading: true,
      bulkOpMessages: [],
      messageIds: [],
      fromUsersFilters: [],
      toContactFilters: [],
      tagsFilters: [],
      messageStatusFilters: [],
      fromSendingDate: null,
      toSendingDate: null,
      searchInSent: '',
      starred: null,
    }
  },
  watch: {
    query() {
      this.getSentEmails()
    }
  },
  created() {
    this.getSentEmails()
    this.store.sendMetrics({name: 'communication_sent', params: this.$route.params, path: this.$route.fullPath})
  },
  computed: {
    sentEmails() {
      return this.state.allSentEmails || []
    },
    messageStatus() {
      return [
        {id: 'pending', name: (this.$t('communication.status_message.pending'))},
        {id: 'failed', name: (this.$t('communication.status_message.failed'))},
        {id: 'delivered', name: (this.$t('communication.status_message.delivered'))},
        {id: 'opened', name: (this.$t('communication.status_message.opened'))},
      ]
    }

  },
  methods: {
    openModal(modal) {
      modal.open()
    },
    getContacts(params) {
      return this.listStore.getContacts(params)
    },
    getUsers(params) {
      return this.listStore.getUsers(params)
    },
    getTags(params) {
      return this.listStore.getTags(params)
    },
    getSentEmails() {
      this.store.getAllSentEmails(this.query).then(() => this.loading = false)
    },
    starMessage(message) {
      return this.store.updateDocumentRequest(message.id, {starred: !message.starred}).then(() => {
        this.store.sendMetrics({name: 'star_document_request', params: this.$route.params, path: this.$route.fullPath})
        this.getSentEmails()
      })
    },
    removeConnectedTag(tag, documentRequestId) {
      this.store.sendMetrics({
        name: 'remove_tag_from_document_request',
        params: this.$route.params,
        path: this.$route.fullPath
      })
      this.store.removeConnectedTag(tag.id, {document_request_id: documentRequestId}).then(() => {
        this.getSentEmails()
      })
    },
    filterMessages() {
      return this.toContactFilters.length > 0 || this.fromUsersFilters.length > 0 ||
        this.tagsFilters.length > 0 || this.messageStatusFilters.length > 0 || this.fromSendingDate !== null ||
        this.toSendingDate !== null || this.searchInSent !== '' || this.starred !== null
    },
    selectAll(e) {
      this.bulkOpMessages = e.target.checked ? this.sentEmails.map(i => i.id) : []
    },
    closeDatePicker() {
      this.$refs.datepicker.close()
    },
    filtersOn() {
      this.$refs.filtersModal.close()
    },
    resetFilters() {
      this.fromUsersFilters = []
      this.toContactFilters = []
      this.tagsFilters = []
      this.messageStatusFilters = []
      this.fromSendingDate = null
      this.toSendingDate = null
      this.searchInSent = ''
      this.starred = null
      this.$router.push({query: null})
      this.$refs.filtersModal.close()
    },
    filterSentEmails() {
      let q = {
        'filters[from]': this.fromUsersFilters.length > 0 ? this.fromUsersFilters.map(c => c.id) : null,
        'filters[to]': this.toContactFilters.length > 0 ? this.toContactFilters.map(c => c.id) : null,
        'filters[status]': this.messageStatusFilters.length > 0 ? this.messageStatusFilters.map(c => c.id) : null,
        'filters[tags]': this.tagsFilters.length > 0 ? this.tagsFilters.map(c => c.id) : null,
        'filters[from_sending_date]': this.fromSendingDate !== null ? this.formatDate(this.fromSendingDate) : null,
        'filters[to_sending_date]': this.toSendingDate !== null ? this.formatDate(this.toSendingDate) : null,
        'filters[search_query]': this.searchInSent.length > 2 ? this.searchInSent : null,
        'filters[starred]': this.starred !== null ? this.starred : null,
        'page': this.query.page || 1
      }
      const f = Object.entries(q).reduce((a, [k, v]) => (v === null ? a : (a[k] = v, a)), {})

      this.$refs.filtersModal.close()
      this.$router.push({query: f})
    },
    openDeleteMessageModal(messageIds) {
      this.messageIds = messageIds
      this.$refs.deleteMessageForm.open()
    },
    deleteSentMessage() {
      this.store.sendMetrics({name: 'delete_document_request', params: this.$route.params, path: this.$route.fullPath})
      this.$refs.deleteMessageForm.close()
      this.store.deleteDocumentRequest(this.messageIds).then(() => {
        this.bulkOpMessages = []
        this.messageIds = []
        this.getSentEmails()
      })
    },
    addTagsToBulkMessages() {
      this.store.sendMetrics({
        name: 'bulk_add_tag_to_document_request',
        params: this.$route.params,
        path: this.$route.fullPath
      })
      this.bulkOpMessages = []
      this.getSentEmails()
    },
    sentVideo(){
      return this.locale == 'de' ? 'https://relatico.wistia.com/medias/7b1lqnfvk8' : 'https://relatico.wistia.com/medias/exn5dtuh5z'
    }
  },
}
</script>

<style lang="sass" scoped>
.actions-on-table
  float: left
  width: 100%

.filter-all-messages
  form
    text-align: left
    label
      text-align: left
      display: block
    .form-group
      margin-bottom: 20px
      .date
        width: 47%
        display: inline-block
        margin-right: 10px
    .form-inline
      label
        width: 20%
        &:first-child
          width: 50%
        &:last-child
          width: 5%
          color: #686868
          cursor: pointer
.files-received
  list-style: none
  padding: 0
  margin: 10px 0 0
  max-width: 450px
  li
    border: 1px solid #c4c4c4
    border-radius: 3px
    padding: 2px 4px
    display: inline-flex
    align-items: center
    margin-right: 5px
    cursor: pointer
    width: 30%
    font-size: 12px
    &:hover
      background: #f2f2f2
    img
      width: 20px
      margin-right: 5px
    a
      color: #667fff
      &.file-name
        text-decoration: none
.bulk-container
  background-color: #EBF4FE
  display: flex
  align-items: center
  padding: 8px 0
  border-radius: 3px
  float: left
  width: 100%
  > span
    padding-left: 10px
  ul
    list-style: none
    padding: 0
    margin: 0
    display: flex
    li
      position: relative
      a
        &:hover, &:visited, &:focus
          background: none
          text-decoration: underline
  &.hide
    display: none
</style>
